import { ArrowRight, Circle } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';
import { useGetClaimTimelineQuery } from '@/services/api/miniTortService';

import { MiniTortStageBadge } from './MiniTortStageBadge';
import type { MiniTortStage, MiniTortTimelineEntry } from './types';

interface MiniTortTimelineProps {
  claimId: string;
  className?: string;
}

export const MiniTortTimeline: React.FC<MiniTortTimelineProps> = ({ claimId, className }) => {
  const { data: entries, isLoading } = useGetClaimTimelineQuery(claimId);

  if (isLoading) {
    return (
      <div className={cn('rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900', className)}>
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Timeline</h3>
        <p className="mt-2 text-sm text-zinc-500">Loading...</p>
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <div className={cn('rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900', className)}>
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Timeline</h3>
        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">No timeline events yet</p>
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900', className)}>
      <h3 className="mb-4 text-sm font-semibold text-zinc-900 dark:text-white">Timeline</h3>
      <div className="space-y-0">
        {entries.map((entry, index) => (
          <TimelineEvent key={`${entry.timestamp}-${index}`} entry={entry} isLast={index === entries.length - 1} />
        ))}
      </div>
    </div>
  );
};

const TimelineEvent: React.FC<{ entry: MiniTortTimelineEntry; isLast: boolean }> = ({
  entry,
  isLast,
}) => {
  const isTransition = entry.event_type === 'stage_changed' && entry.old_stage && entry.new_stage;

  return (
    <div className="flex gap-3">
      {/* Line + dot */}
      <div className="flex flex-col items-center">
        <Circle className="h-2.5 w-2.5 shrink-0 fill-indigo-500 text-indigo-500" />
        {!isLast && <div className="w-px flex-1 bg-zinc-200 dark:bg-zinc-700" />}
      </div>

      {/* Content */}
      <div className={cn('pb-4', isLast && 'pb-0')}>
        <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200">{entry.description}</p>

        {isTransition && (
          <div className="mt-1 flex items-center gap-2">
            <MiniTortStageBadge stage={entry.old_stage as MiniTortStage} size="sm" />
            <ArrowRight className="h-3 w-3 text-zinc-400" />
            <MiniTortStageBadge stage={entry.new_stage as MiniTortStage} size="sm" />
          </div>
        )}

        <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
          {new Date(entry.timestamp).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </p>
      </div>
    </div>
  );
};
